import type { IconType } from 'react-icons';

import type {
  Transaction,
  TransactionCategory,
} from '@/store/expenses-store/transactions/transactions.dto';
import { parseAmount } from '@/utils/form';

import { isInflow } from '../TransactionsPage/utils/transactionColors';

import { resolveIcon } from './iconRegistry';

export const UNCATEGORIZED_COLOR = '#94A3B8';

export interface CategorySpending {
  slug: string;
  title: string;
  icon: IconType;
  color: string;
  spent: number;
  /** Backend category this row maps to; undefined for stale/deleted slugs not in the list. */
  category?: TransactionCategory;
}

/**
 * Sum outflow (expenses/outgoing) per category, seeded from the category list so
 * budgeted-but-unspent categories still surface, sorted by spend descending.
 * Canonical spending aggregation — shared by the Spending page and the Budget widget.
 *
 * `expenses_category` carries the category id since wallet gave every vessel its own
 * copies of the default categories; a slug is still accepted so rows written before
 * that migration, and any caller still passing one, keep landing on their category.
 */
export function aggregateSpendingByCategory(
  transactions: Transaction[],
  categories: TransactionCategory[],
): CategorySpending[] {
  const rows = new Map<string, CategorySpending>();
  const slugByRef = new Map<string, string>();

  categories.forEach((cat) => {
    rows.set(cat.slug, {
      slug: cat.slug,
      title: cat.title,
      icon: resolveIcon(cat.icon),
      color: cat.color,
      spent: 0,
      category: cat,
    });
    slugByRef.set(cat.id, cat.slug);
    slugByRef.set(cat.slug, cat.slug);
  });

  transactions.forEach((tx) => {
    if (isInflow(tx.type)) return; // outflow only (expenses/outgoing)
    const ref = tx.expenses_category?.trim() || 'other';
    const slug = slugByRef.get(ref) ?? ref;
    const amount = Math.abs(Number.parseFloat(tx.amount?.number || '0'));
    if (Number.isNaN(amount)) return;

    const row = rows.get(slug) ?? {
      slug,
      title: slug,
      icon: resolveIcon(undefined),
      color: UNCATEGORIZED_COLOR,
      spent: 0,
    };
    row.spent += amount;
    rows.set(slug, row);
  });

  // Rounded back to cents so float drift from summing decimal amounts never leaks into totals.
  return [...rows.values()]
    .map((row) => ({ ...row, spent: parseAmount(row.spent) }))
    .sort((a, b) => b.spent - a.spent);
}
